/**
 * How the side-panel tabs fit on one line (SideTabs.tsx), in three steps:
 * 1. every tab with its label, when they all fit;
 * 2. else compact: tabs that have an icon show only the icon (and badge), the label moves to the tooltip;
 * 3. else compact, and the open tab plus as many of the others, in order, as fit next to the "More" button; the rest
 *    go in its menu.
 * Pure (node --test).
 */

export interface TabFit {
  /** Indices of the tabs shown on the bar, in their order. */
  shown: number[];
  /** Tabs with an icon show only the icon. */
  compact: boolean;
}

const fits = (widths: number[], available: number) => widths.reduce((sum, w) => sum + w, 0) <= available + 0.5;

/** `full` and `compact` are the tabs' natural widths in px (the same for a tab without an icon). */
export function fitTabs(full: number[], compact: number[], available: number, moreWidth: number, active: number): TabFit {
  const all = full.map((_, i) => i);
  if (fits(full, available)) return { shown: all, compact: false };
  if (fits(compact, available)) return { shown: all, compact: true };
  let room = available - moreWidth;
  const keep = new Set<number>();
  if (active >= 0 && active < compact.length) {
    keep.add(active); // the open tab always stays visible, even when only it fits
    room -= compact[active]!;
  }
  for (const i of all) {
    if (keep.has(i)) continue;
    if (compact[i]! > room + 0.5) break; // keep the order: no later, narrower tab jumps the queue
    keep.add(i);
    room -= compact[i]!;
  }
  return { shown: all.filter((i) => keep.has(i)), compact: true };
}
